import _ from 'lodash'

export const useBilibili = () => {
	const {getFileNameFromUrl, extFetch, getCookies} = useHelpers()
	/**
	 * Extract video URL from bilibili page
	 * Supports both direct video URLs and page URLs
	 */
	async function getVideoFromUrl(url) {
		try {
			// Use extFetch to get the page content through the extension
			let response = await extFetch(url, {
				method: 'GET',
				headers: {
					'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/110.0.0.0 Safari/537.36',
					'Referer': 'https://www.bilibili.com/'
				}
			})

			if (!response || !response.body) {
				throw new Error('Failed to fetch Bilibili page')
			}

			let html = typeof response.body === 'string' ? response.body : (response.body.toString ? response.body.toString() : String(response.body))
			
			// Extract play info from HTML
			let playInfo;
			const match = html.match(/window\.__playinfo__=({.*?})<\/script>/);
			if (match) {
				playInfo = JSON.parse(match[1]);
			} else {
				const match2 = html.match(/<script>window\.__playinfo__=({.*?})<\/script>/);
				if (match2) {
					playInfo = JSON.parse(match2[1]);
				}
			}

			if (!playInfo) {
				// Try another pattern if the above fails
				const match3 = html.match(/__playinfo__=({.*?});/);
				if (match3) {
					playInfo = JSON.parse(match3[1]);
				}
			}

			if (!playInfo) {
				throw new Error('Could not find __playinfo__ in the Bilibili page');
			}

			const dash = playInfo.data?.dash;
			if (!dash) {
				throw new Error('No DASH streams found in __playinfo__');
			}

			// Pick the first video and audio stream
			const video = dash.video[0];
			const audio = dash.audio[0];

			if (!video || !audio) {
				throw new Error('Incomplete DASH streams found');
			}

			const videoUrl = video.baseUrl || video.base_url;
			const audioUrl = audio.baseUrl || audio.base_url;

			// Get cookies from the extension for Bilibili
			const cookies = await getCookies({url: 'https://www.bilibili.com'})
			const cookieString = cookies.map(c => `${c.name}=${c.value}`).join('; ');

			return {
				website: 'bilibili.com',
				type: 'video',
				url: url,
				dash: {
					videoUrl,
					audioUrl,
					referer: url,
					cookie: cookieString,
					userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/110.0.0.0 Safari/537.36'
				},
				fileName: `bilibili_${Date.now()}.mp4`,
				previewUrl: videoUrl,
				thumbnail: null,
				canView: true,
				source: 'bilibili'
			}
		} catch (error) {
			console.error('Error fetching Bilibili video:', error)
			throw error
		}
	}

	/**
	 * Process multiple URLs and extract videos
	 */
	async function getVideosFromUrls(urls) {
		// Filter out empty lines and trim
		let validUrls = urls
			.split('\n')
			.map(url => url.trim())
			.filter(url => url && url.length > 0)

		// Process all URLs
		let results = []
		for (let url of validUrls) {
			try {
				let media = await getVideoFromUrl(url)
				results.push(media)
			} catch (e) {
				console.warn(`Failed to process Bilibili URL: ${url}`, e)
			}
		}

		// Add IDs to each media item
		return results.map((media, index) => ({
			...media,
			id: `bilibili-${Date.now()}-${index}`,
			source: 'bilibili'
		}))
	}

	return {
		getMedia: async (form) => {
			if (form.urls) return getVideosFromUrls(form.urls)
			if (form.url) {
				let media = await getVideoFromUrl(form.url)
				media.id = `bilibili-${Date.now()}`
				media.source = 'bilibili'
				return [media]
			}
			return []
		},
		getVideoFromUrl,
		getVideosFromUrls
	}
}
